import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import compact from 'lodash/compact'
import omit from 'lodash/omit'
import {
  BookableShipmentSavedSearch,
  GetBookableShipmentSavedSearchesResponse,
  TBookableShipmentSavedSearchModalMode,
  useCreateBookableShipmentSavedSearch,
  useDeleteBookableShipmentSavedSearch,
  useGetBookableShipmentSavedSearches,
} from 'apps/fleet-planner/types'
import { Toast, useToastState } from 'transfix-ui'
import { TruckAvailabilityModal } from '../components/modals'
import { compareBookableShipmentSavedSearch } from '../utils/bookableShipmentSavedSearch'
import { UserContext } from './UserContext'

interface IBookableShipmentSavedSearchState {
  savedSearches: BookableShipmentSavedSearch[]
  isSavedSearchesLoading: boolean
  isSavedSearchesEmpty: boolean
  saveSearch: (search: BookableShipmentSavedSearch) => Promise<void>
  deleteSearch: (search: BookableShipmentSavedSearch['id']) => Promise<void>
  refetchBookableShipmentSavedSearches: () => Promise<GetBookableShipmentSavedSearchesResponse | null>
  openTruckAvailabilityModal: (pickupDate?: string) => void
}

const BookableShipmentSavedSearchContext = createContext<IBookableShipmentSavedSearchState>({
  savedSearches: [],
  saveSearch: async () => {},
  deleteSearch: async () => {},
  refetchBookableShipmentSavedSearches: async () => Promise.resolve(null),
  isSavedSearchesLoading: false,
  isSavedSearchesEmpty: false,
  openTruckAvailabilityModal: () => undefined,
})

interface IBookableShipmentSavedSearchProvider {
  children: ReactNode
}

export const BookableShipmentSavedSearchProvider = ({
  children,
}: IBookableShipmentSavedSearchProvider) => {
  const [savedSearches, setSavedSearches] = useState<BookableShipmentSavedSearch[]>([])
  const [isSavedSearchesLoading, setIsSavedSearchesLoading] = useState<boolean>(true)
  const [modalMode, setModalMode] = useState<TBookableShipmentSavedSearchModalMode>()
  const [truckAvailablityPickupDate, setTruckAvailablityPickupDate] = useState<string | undefined>()
  const { updateUserOnboarding, userFlags } = useContext(UserContext)

  const {
    data: getData,
    loading: isGetLoading,
    refetch: refetchBookableShipmentSavedSearches,
  } = useGetBookableShipmentSavedSearches({})
  const { mutate: deleteSavedSearch } = useDeleteBookableShipmentSavedSearch({})
  const { mutate: createSavedShipment } = useCreateBookableShipmentSavedSearch({})
  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()

  const isSavedSearchesEmpty = useMemo(
    () => !isSavedSearchesLoading && savedSearches.length === 0,
    [savedSearches.length, isSavedSearchesLoading]
  )

  const saveSearch = async (search: BookableShipmentSavedSearch) => {
    // pickupTimes is a filter that should only be used for reloads searches
    // check and remove pickupTimes filter to prevent it from being added to a savedSearch create.
    if (search.pickupTimes) {
      delete search.pickupTimes
    }
    const hasDays = !!search.pickupIsoDaysOfWeek?.length
    const hasDates = !!search.pickupStartDate
    const requestData = []
    if (hasDays && hasDates) {
      // split the the request into two, one for dates and one for days
      requestData.push(omit(search, 'pickupStartDate', 'pickupEndDate', 'deliveryEndDate'))
      requestData.push(omit(search, 'pickupIsoDaysOfWeek'))
    } else {
      requestData.push(search)
    }
    // dispatch post requests
    let responseData: BookableShipmentSavedSearch[] = []
    try {
      setIsSavedSearchesLoading(true)
      const responses = requestData.map(bookableShipmentSavedSearch =>
        createSavedShipment({ bookableShipmentSavedSearch })
      )
      responseData = compact(
        (await Promise.all(responses)).map(result => result.bookableShipmentSavedSearch)
      )
    } finally {
      setIsSavedSearchesLoading(false)
    }
    // update saved searches state
    if (responseData.length) {
      setSavedSearches(currentSavedSearches =>
        [...currentSavedSearches, ...responseData].sort(compareBookableShipmentSavedSearch)
      )
      openToast({
        toastType: 'default',
        toastMessage: "You'll receive emails about matching loads.",
      })
    }
  }

  const deleteSearch = async (id: BookableShipmentSavedSearch['id']) => {
    if (id) {
      await deleteSavedSearch(id)
      await refetchBookableShipmentSavedSearches()
    }
    openToast({
      toastType: 'default',
      toastMessage: 'Lane deleted successfully',
    })
  }

  // load saved searches on mount
  useEffect(() => {
    setIsSavedSearchesLoading(isGetLoading)
    if (!isGetLoading) {
      setSavedSearches(getData?.bookableShipmentSavedSearches ?? [])
    }
  }, [getData?.bookableShipmentSavedSearches, isGetLoading])

  const handleCloseModal = async () => {
    try {
      if (!userFlags.truckAvailabilityDismissed) {
        await updateUserOnboarding({ onboarding: { truckAvailabilityDismissed: true } })
      }
    } finally {
      setModalMode(undefined)
    }
  }

  const handleTruckAvailablityCommit = async (savedSearch: BookableShipmentSavedSearch) => {
    try {
      await saveSearch(savedSearch)
      openToast({
        toastType: 'default',
        toastMessage: "You'll receive emails about loads matching this truck.",
      })
      handleCloseModal()
    } catch (e) {
      openToast({
        toastType: 'default',
        toastMessage: 'Something went wront. Please try again.',
      })
    }
  }

  const openTruckAvailabilityModal = (pickupDate?: string) => {
    setTruckAvailablityPickupDate(pickupDate)
    setModalMode('dispatcher_truck_availability')
  }

  const state = {
    savedSearches,
    saveSearch,
    isSavedSearchesEmpty,
    isSavedSearchesLoading,
    deleteSearch,
    refetchBookableShipmentSavedSearches,
    openTruckAvailabilityModal,
  }

  return (
    <BookableShipmentSavedSearchContext.Provider value={state}>
      {children}
      <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
      {modalMode === 'dispatcher_truck_availability' && (
        <TruckAvailabilityModal
          onClose={handleCloseModal}
          onConfirm={handleTruckAvailablityCommit}
          pickupDate={truckAvailablityPickupDate}
        />
      )}
    </BookableShipmentSavedSearchContext.Provider>
  )
}

export const useBookableShipmentSavedSearchContext = () =>
  useContext(BookableShipmentSavedSearchContext)
